import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Text } from '@fly/core';
import { PACKAGE_MANAGERS } from '@fly/shared';
import { FlyCodeCopy } from '@fly/shared-ui';
import { FlySvgSprite } from '@fly/svg';
import { ScrollArea, Separator } from '@radix-ui/themes';
import cn from 'classnames';

import { DistributionFormActiveRow } from './DistributionFormActiveRow';
import { DistributionFormComingSoon } from './DistributionFormComingSoon';
import { DistributionFormFiles } from './DistributionFormFiles';
import type { DistributionFormRow } from './distributionFormKeys';
import { distributionRowKey } from './distributionFormKeys';

export { distributionRowKey } from './distributionFormKeys';

export interface DistributionFormProps {
  /** Re-opens the manage-distribution dialog after a nested confirm dialog closes. */
  reopenDistributionDialog?: () => void;
  artifactVersions: DistributionFormRow[];
  releaseId?: string;
  releaseName?: string;
}

export function DistributionForm({
  artifactVersions,
  reopenDistributionDialog,
  releaseId,
  releaseName,
}: DistributionFormProps) {
  const { t } = useTranslation();

  const supportedArtifactVersions = useMemo(
    () => artifactVersions.filter((row) => row.isSupported),
    [artifactVersions]
  );
  const notSupportedArtifactVersions = useMemo(
    () => artifactVersions.filter((row) => !row.isSupported),
    [artifactVersions]
  );
  const supportedArtifactBlocks = supportedArtifactVersions.map((row) => (
    <div
      key={distributionRowKey(row)}
      className='flex flex-col gap-fly-4'
      data-testid='distribution-form-artifact-block'
    >
      <DistributionFormActiveRow
        row={row}
        reopenDistributionDialog={reopenDistributionDialog}
      />
      {(row.packageType === PACKAGE_MANAGERS.DOCKER ||
        row.packageType === PACKAGE_MANAGERS.OCI) &&
        row.distributionUrl &&
        !releaseId && (
          <div data-testid='distribution-docker-pull-command'>
            <FlyCodeCopy
              code={row.distributionUrl}
              className='w-full'
              highContrast
            />
          </div>
        )}
      {row.packageType === PACKAGE_MANAGERS.GENERIC && !releaseId && (
        <DistributionFormFiles
          artifactName={row.artifactName}
          versionName={row.versionName}
          packageType={row.packageType}
        />
      )}
    </div>
  ));

  return (
    <div
      className='flex flex-col gap-fly-4 overflow-auto'
      data-testid='artifact-version-distribution-form'
    >
      {releaseName && (
        <div className='flex'>
          <div className='flex items-center justify-between gap-fly-3'>
            <div className='min-w-0 flex items-center gap-fly-3'>
              <FlySvgSprite name='release' size={16} />
              <Text size='3' weight='semibold' color='neutral' highContrast>
                {releaseName}
              </Text>
            </div>
          </div>
        </div>
      )}
      <ScrollArea
        scrollbars='vertical'
        type='auto'
        className={cn(
          'max-h-[300px]',
          '[&_.rt-ScrollAreaViewport>div]:max-w-full',
          !!releaseId && 'pr-[20px] pl-fly-6'
        )}
      >
        <div className='flex flex-col gap-fly-3'>
          <div className='flex max-w-full flex-col gap-fly-3'>
            {supportedArtifactBlocks}
            <DistributionFormComingSoon
              comingSoonArtifactVersions={notSupportedArtifactVersions ?? []}
            />
          </div>
        </div>
      </ScrollArea>
      <Separator
        orientation='horizontal'
        className='w-full bg-[var(--slate-a5)]'
      />
      <Badge
        size='1'
        iconStart='distribution'
        iconStartSize={16}
        variant='ghost'
        color='neutral'
      >
        {t('app.dashboard.artifacts.distribution_form.making', {
          defaultValue:
            'Making this artifact public will make it accessible to anyone.',
        })}
      </Badge>
    </div>
  );
}
